import { useLingui } from '@lingui/react/macro';
import { styled } from '@linaria/react';
import { APP_PATHS } from 'app-shared/constants';
import { Dropdown } from 'app-ui/components';
import { IconColorSwatch, IconLogout, IconUser } from 'app-ui/icon';
import { Avatar, Pill } from 'app-ui/primitives/data-display';
import { MOBILE_VIEWPORT, themeCssVariables } from 'app-ui/theme';
import { Link } from 'react-router-dom';

import { AUTH_MODE } from '@/auth/constants/AuthMode';
import { ClerkManageAccountItem } from '@/navigation/components/ClerkManageAccountItem';
import { ClerkSignOutItem } from '@/navigation/components/ClerkSignOutItem';
import { useCurrentUser } from '@/session/hooks/useCurrentUser';
import { getFullName } from '@/session/utils/getFullName';
import { useIsNavigationDrawerCollapsed } from '@/ui/layout/navigation-drawer/hooks/useIsNavigationDrawerCollapsed';
import { isMobileNavigationDrawerOpenState } from '@/ui/layout/navigation-drawer/states/isMobileNavigationDrawerOpenState';
import { HotkeyLayerEffect } from '@/ui/utilities/hotkey/effect-components/HotkeyLayerEffect';
import { useSetAtomState } from '@/ui/utilities/state/jotai/hooks/useSetAtomState';

const StyledTrigger = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: ${themeCssVariables.border.radius.md};
  box-sizing: border-box;
  color: ${themeCssVariables.font.color.secondary};
  cursor: pointer;
  display: flex;
  font-family: inherit;
  font-size: ${themeCssVariables.font.size.md};
  font-weight: ${themeCssVariables.font.weight.medium};
  gap: ${themeCssVariables.spacing[2]};
  height: ${themeCssVariables.spacing[7]};
  min-width: 0;
  outline: none;
  padding: 0 ${themeCssVariables.spacing[2]};
  transition: ${themeCssVariables.clickableElementBackgroundTransition};
  width: 100%;

  &[data-popup-open],
  &:focus-visible {
    background: ${themeCssVariables.background.transparent.light};
    color: ${themeCssVariables.font.color.primary};
  }

  &[data-collapsed] {
    justify-content: center;
    padding: 0;
    width: ${themeCssVariables.spacing[8]};
  }

  @media (hover: hover) {
    &:hover {
      background: ${themeCssVariables.background.transparent.light};
      color: ${themeCssVariables.font.color.primary};
    }
  }

  @media (max-width: ${MOBILE_VIEWPORT}px) {
    height: ${themeCssVariables.spacing[11]};
  }
`;

const StyledMenuContent = styled(Dropdown.Content)`
  @media (max-width: ${MOBILE_VIEWPORT}px) {
    [role='menuitem'],
    [role='menuitemradio'] {
      min-height: ${themeCssVariables.spacing[11]};
    }
  }
`;
const StyledName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledUserSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${themeCssVariables.spacing[0.5]};
  min-width: 0;
  padding: ${themeCssVariables.spacing[2]} ${themeCssVariables.spacing[3]};
`;

const StyledSummaryName = styled.span`
  color: ${themeCssVariables.font.color.primary};
  font-size: ${themeCssVariables.font.size.md};
  font-weight: ${themeCssVariables.font.weight.medium};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledSummaryEmail = styled.span`
  color: ${themeCssVariables.font.color.tertiary};
  font-size: ${themeCssVariables.font.size.sm};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CurrentUserMenu = () => {
  const { t } = useLingui();
  const currentUser = useCurrentUser();
  const isCollapsed = useIsNavigationDrawerCollapsed();
  const setIsMobileNavigationDrawerOpen = useSetAtomState(
    isMobileNavigationDrawerOpenState,
  );
  const fullName = getFullName(currentUser);

  const handleLinkClick = () => {
    setIsMobileNavigationDrawerOpen(false);
  };

  return (
    <Dropdown.Root type="menu">
      <Dropdown.Trigger
        aria-label={t`Account menu`}
        render={<StyledTrigger data-collapsed={isCollapsed || undefined} />}
      >
        <Avatar name={fullName} size="md" shape="circle" />
        {!isCollapsed && <StyledName>{fullName}</StyledName>}
      </Dropdown.Trigger>
      <StyledMenuContent
        aria-label={t`Account`}
        width={240}
        side="top"
        sideOffset={4}
      >
        <HotkeyLayerEffect layer="overlay" />
        <StyledUserSummary role="presentation">
          <StyledSummaryName>{fullName}</StyledSummaryName>
          <StyledSummaryEmail>{currentUser.email}</StyledSummaryEmail>
        </StyledUserSummary>
        <Dropdown.Separator />
        <Dropdown.Section>
          <Dropdown.ActionItem
            startIcon={<IconUser />}
            render={<Link to={APP_PATHS.settingsProfile} />}
            onClick={handleLinkClick}
          >
            {t`Profile`}
          </Dropdown.ActionItem>
          <Dropdown.ActionItem
            startIcon={<IconColorSwatch />}
            render={<Link to={APP_PATHS.settingsAppearance} />}
            onClick={handleLinkClick}
          >
            {t`Appearance`}
          </Dropdown.ActionItem>
          {AUTH_MODE === 'clerk' && <ClerkManageAccountItem />}
        </Dropdown.Section>
        <Dropdown.Separator />
        <Dropdown.Section>
          {AUTH_MODE === 'clerk' ? (
            <ClerkSignOutItem />
          ) : (
            <Dropdown.ActionItem
              disabled
              startIcon={<IconLogout />}
              endIcon={<Pill label={t`Soon`} />}
            >
              {t`Sign out`}
            </Dropdown.ActionItem>
          )}
        </Dropdown.Section>
      </StyledMenuContent>
    </Dropdown.Root>
  );
};
